import React, { useState } from 'react';
import {
    Globe,
    BarChart3,
    FileText,
    Smartphone,
    Star,
    Play,
    Users,
    TrendingUp,
    Calendar,
    Bell,
    Search,
    Menu,
    ChevronRight,
    Download,
    Eye,
    Heart,
    MessageCircle,
    Share2,
    Settings,
    Plus,
    CheckCircle,
    AlertCircle,
    Clock,
    Shield,
    Zap,
    Target,
    Award,
    Briefcase,
    Monitor,
    Coffee,
    MapPin,
    Mail,
    Phone,
    ArrowRight,
    Home,
    ShoppingBag,
    User,
    Send,
    PlusCircle
} from 'lucide-react';

export const TemplatesDemo = ({ colors }) => {
    const [activeTemplate, setActiveTemplate] = useState('landing');

    // Template tabs configuration
    const templateTabs = [
        { id: 'landing', label: 'Landing Page', icon: Globe },
        { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
        { id: 'mobile', label: 'Mobile App', icon: Smartphone },
        { id: 'ecommerce', label: 'E-commerce', icon: ShoppingBag },
        { id: 'pricing', label: 'Pricing', icon: Target },
        { id: 'blog', label: 'Blog', icon: FileText },
        { id: 'portfolio', label: 'Portfolio', icon: Briefcase },
        { id: 'email', label: 'Email', icon: Mail },
        { id: 'auth', label: 'Login', icon: Shield }
    ];

    // Utility function to get colors with fallback
    const getColor = (index) => colors[index]?.value || colors[0]?.value || '#3b82f6';

    // Convert hex to RGBA for transparency
    const hexToRgba = (hex, alpha = 1) => {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        if (!result) return hex;
        const r = parseInt(result[1], 16);
        const g = parseInt(result[2], 16);
        const b = parseInt(result[3], 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Color hierarchy mapping for semantic use
    const colorScheme = {
        primary: getColor(0),      // Primary brand color
        secondary: getColor(1),    // Secondary brand color
        accent: getColor(2),       // Accent/highlight color
        success: getColor(3),      // Success states
        warning: getColor(4),      // Warning states
        info: getColor(5),         // Info states
        neutral: getColor(6),      // Neutral elements
        surface: getColor(7)       // Background surfaces
    };

    const renderLandingPage = () => (
        <div className="bg-white dark:bg-gray-900 min-h-screen">
            {/* Navigation - unchanged */}
            <nav className="relative z-10 bg-white dark:bg-gray-900 shadow-sm border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                B
                            </div>
                            <span className="ml-2 text-xl font-semibold text-gray-900 dark:text-white">Brand</span>
                        </div>
                        <div className="hidden lg:flex items-center space-x-8">
                            <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium">Home</a>
                            <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium">About</a>
                            <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium">Services</a>
                            <a href="#" className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium">Contact</a>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                Sign In
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all hover:opacity-90 focus:ring-4 focus:outline-none"
                                style={{
                                    backgroundColor: colorScheme.primary,
                                    boxShadow: `0 4px 14px 0 ${hexToRgba(colorScheme.primary, 0.25)}`
                                }}
                            >
                                Get Started
                            </button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section - enhanced */}
            <div
                className="relative pt-12 pb-20 overflow-hidden"
                style={{
                    background: `linear-gradient(135deg, ${hexToRgba(colorScheme.primary, 0.05)} 0%, ${hexToRgba(colorScheme.secondary, 0.05)} 100%)`
                }}
            >
                <div className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
                    <div className="text-center">
                        <div
                            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium text-white mb-6 shadow-lg"
                            style={{ backgroundColor: colorScheme.accent }}
                        >
                            <Star className="w-4 h-4 mr-2" />
                            New Feature Launch - AI-Powered Design
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
                            Build Amazing
                            <span
                                className="block mt-2 bg-gradient-to-r bg-clip-text text-transparent"
                                style={{
                                    backgroundImage: `linear-gradient(135deg, ${colorScheme.primary} 0%, ${colorScheme.secondary} 100%)`
                                }}
                            >
                                Digital Experiences
                            </span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-600 dark:text-gray-400 mb-8 max-w-4xl mx-auto leading-relaxed">
                            Transform your ideas into stunning digital products that captivate users and drive business growth.
                            Join over <span className="font-semibold text-gray-900 dark:text-white">10,000+ teams</span> building
                            the future with our advanced design platform.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
                            <button
                                className="w-full sm:w-auto px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all hover:opacity-90 hover:scale-105 focus:ring-4 focus:outline-none flex items-center justify-center space-x-2 shadow-xl"
                                style={{
                                    backgroundColor: colorScheme.primary,
                                    boxShadow: `0 10px 25px 0 ${hexToRgba(colorScheme.primary, 0.3)}`
                                }}
                            >
                                <Play className="w-5 h-5" />
                                <span>Start Building Free</span>
                            </button>
                            <button
                                className="w-full sm:w-auto px-8 py-4 text-lg font-semibold rounded-lg border-2 transition-all hover:bg-gray-50 dark:hover:bg-gray-800 hover:scale-105 flex items-center justify-center space-x-2"
                                style={{
                                    borderColor: colorScheme.primary,
                                    color: colorScheme.primary
                                }}
                            >
                                <Eye className="w-5 h-5" />
                                <span>Watch 2-min Demo</span>
                            </button>
                        </div>

                        {/* Enhanced Trust Indicators */}
                        <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-gray-500 dark:text-gray-400 mb-12">
                            <div className="flex items-center space-x-2">
                                <Shield className="w-4 h-4" style={{ color: colorScheme.success }} />
                                <span>SOC2 Compliant</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Zap className="w-4 h-4" style={{ color: colorScheme.warning }} />
                                <span>99.99% Uptime SLA</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Award className="w-4 h-4" style={{ color: colorScheme.info }} />
                                <span>G2 Leader 2024</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4" style={{ color: colorScheme.secondary }} />
                                <span>10M+ Active Users</span>
                            </div>
                        </div>

                        {/* Customer Logos */}
                        <div className="text-center">
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">Trusted by leading companies worldwide</p>
                            <div className="flex flex-wrap justify-center items-center gap-8 opacity-60 grayscale hover:opacity-80 hover:grayscale-0 transition-all duration-500">
                                {['Google', 'Microsoft', 'Apple', 'Tesla', 'Netflix', 'Spotify'].map((company) => (
                                    <div key={company} className="px-6 py-3 bg-gray-100 dark:bg-gray-800 rounded-lg font-semibold text-gray-700 dark:text-gray-300">
                                        {company}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Floating Elements */}
                <div className="absolute top-20 left-10 w-20 h-20 rounded-full opacity-20 animate-pulse" style={{ backgroundColor: colorScheme.accent }}></div>
                <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full opacity-10 animate-pulse" style={{ backgroundColor: colorScheme.secondary, animationDelay: '1s' }}></div>
                <div className="absolute top-40 right-20 w-16 h-16 rounded-full opacity-15 animate-pulse" style={{ backgroundColor: colorScheme.success, animationDelay: '2s' }}></div>
                <div className="absolute top-60 left-1/4 w-12 h-12 rounded-full opacity-20 animate-pulse" style={{ backgroundColor: colorScheme.warning, animationDelay: '0.5s' }}></div>
            </div>

            {/* Features Section - enhanced */}
            <div className="py-20 bg-gray-50 dark:bg-gray-800">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-16">
                        <div
                            className="inline-block px-4 py-2 rounded-full text-sm font-medium text-white mb-4"
                            style={{ backgroundColor: colorScheme.secondary }}
                        >
                            Why Choose Us
                        </div>
                        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                            Everything You Need to Succeed
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                            Powerful features designed to streamline your workflow, enhance collaboration,
                            and accelerate your path to market with professional-grade tools and insights.
                        </p>
                    </div>

                    {/* Main Features Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
                        {[
                            {
                                icon: Users,
                                title: "Real-time Collaboration",
                                desc: "Work seamlessly with your team using live cursors, instant comments, version history, and conflict-free editing across all devices",
                                color: 'primary',
                                features: ['Live cursors', 'Instant sync', 'Version control', 'Team workspace']
                            },
                            {
                                icon: TrendingUp,
                                title: "Advanced Analytics",
                                desc: "Get deep insights with custom dashboards, user behavior tracking, A/B testing, and comprehensive reporting tools",
                                color: 'secondary',
                                features: ['Custom dashboards', 'A/B testing', 'User tracking', 'Export reports']
                            },
                            {
                                icon: Settings,
                                title: "Enterprise Customization",
                                desc: "Tailor every aspect with white-label options, custom integrations, advanced permissions, and dedicated support",
                                color: 'accent',
                                features: ['White labeling', 'API access', 'SSO integration', '24/7 support']
                            }
                        ].map((feature, index) => (
                            <div key={index} className="bg-white dark:bg-gray-900 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 hover:shadow-2xl hover:scale-105 transition-all duration-300 group">
                                <div
                                    className="w-16 h-16 rounded-xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg"
                                    style={{ backgroundColor: colorScheme[feature.color] }}
                                >
                                    <feature.icon className="w-8 h-8" />
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                                    {feature.title}
                                </h3>
                                <p className="text-gray-600 dark:text-gray-400 mb-6 leading-relaxed">
                                    {feature.desc}
                                </p>
                                <ul className="space-y-2 mb-6">
                                    {feature.features.map((item, idx) => (
                                        <li key={idx} className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                                            <CheckCircle className="w-4 h-4 mr-2" style={{ color: colorScheme[feature.color] }} />
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className="flex items-center text-sm font-semibold group-hover:translate-x-2 transition-all duration-300"
                                    style={{ color: colorScheme[feature.color] }}
                                >
                                    Learn more <ArrowRight className="w-4 h-4 ml-1" />
                                </button>
                            </div>
                        ))}
                    </div>

                    {/* Additional Features Grid */}
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            { icon: Shield, title: 'Security First', desc: 'End-to-end encryption' },
                            { icon: Zap, title: 'Lightning Fast', desc: 'Sub-second response times' },
                            { icon: Globe, title: 'Global CDN', desc: '99.99% uptime guarantee' },
                            { icon: Award, title: 'Award Winning', desc: 'Industry recognition' }
                        ].map((item, index) => (
                            <div key={index} className="text-center p-6 bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all duration-300">
                                <div
                                    className="w-12 h-12 rounded-lg flex items-center justify-center mx-auto mb-4"
                                    style={{ backgroundColor: hexToRgba(colorScheme.primary, 0.1) }}
                                >
                                    <item.icon className="w-6 h-6" style={{ color: colorScheme.primary }} />
                                </div>
                                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">{item.title}</h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Testimonials Section - NEW */}
            <div className="py-20 bg-white dark:bg-gray-900">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                            Loved by Teams Worldwide
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                            See what our customers say about their experience building with our platform
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {[
                            {
                                quote: "This platform transformed how our design team collaborates. We ship features 3x faster now.",
                                author: "Sarah Chen",
                                role: "Head of Design",
                                company: "TechCorp",
                                rating: 5
                            },
                            {
                                quote: "The analytics insights helped us increase user engagement by 40% in just two months.",
                                author: "Michael Rodriguez",
                                role: "Product Manager",
                                company: "StartupXYZ",
                                rating: 5
                            },
                            {
                                quote: "Best investment we made this year. The ROI was clear within the first quarter.",
                                author: "Emily Johnson",
                                role: "VP of Engineering",
                                company: "ScaleInc",
                                rating: 5
                            }
                        ].map((testimonial, index) => (
                            <div key={index} className="bg-gray-50 dark:bg-gray-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700">
                                <div className="flex items-center mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-5 h-5 fill-current" style={{ color: colorScheme.warning }} />
                                    ))}
                                </div>
                                <blockquote className="text-gray-700 dark:text-gray-300 mb-6 text-lg leading-relaxed">
                                    "{testimonial.quote}"
                                </blockquote>
                                <div className="flex items-center">
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold mr-4"
                                        style={{ backgroundColor: colorScheme.primary }}
                                    >
                                        {testimonial.author.split(' ').map(n => n[0]).join('')}
                                    </div>
                                    <div>
                                        <div className="font-semibold text-gray-900 dark:text-white">{testimonial.author}</div>
                                        <div className="text-sm text-gray-600 dark:text-gray-400">{testimonial.role} at {testimonial.company}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Pricing Section - NEW */}
            <div className="py-20 bg-gray-50 dark:bg-gray-800">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                            Simple, Transparent Pricing
                        </h2>
                        <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                            Choose the plan that fits your team size and needs. All plans include core features.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {[
                            {
                                name: "Starter",
                                price: "$19",
                                period: "/month",
                                description: "Perfect for small teams getting started",
                                features: ["Up to 5 team members", "10 projects", "Basic analytics", "Email support"],
                                popular: false
                            },
                            {
                                name: "Professional",
                                price: "$49",
                                period: "/month",
                                description: "Best for growing teams and businesses",
                                features: ["Up to 25 team members", "Unlimited projects", "Advanced analytics", "Priority support", "Custom integrations"],
                                popular: true
                            },
                            {
                                name: "Enterprise",
                                price: "Custom",
                                period: "",
                                description: "For large organizations with specific needs",
                                features: ["Unlimited team members", "Custom features", "Dedicated support", "SLA guarantee", "On-premise option"],
                                popular: false
                            }
                        ].map((plan, index) => (
                            <div key={index} className={`bg-white dark:bg-gray-900 p-8 rounded-2xl border-2 relative ${plan.popular ? 'border-blue-500 shadow-2xl scale-105' : 'border-gray-200 dark:border-gray-700'} hover:shadow-xl transition-all duration-300`}>
                                {plan.popular && (
                                    <div
                                        className="absolute -top-4 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-full text-white text-sm font-medium"
                                        style={{ backgroundColor: colorScheme.primary }}
                                    >
                                        Most Popular
                                    </div>
                                )}
                                <div className="text-center mb-8">
                                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                                    <p className="text-gray-600 dark:text-gray-400 mb-4">{plan.description}</p>
                                    <div className="flex items-baseline justify-center">
                                        <span className="text-4xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                                        <span className="text-gray-600 dark:text-gray-400 ml-1">{plan.period}</span>
                                    </div>
                                </div>
                                <ul className="space-y-3 mb-8">
                                    {plan.features.map((feature, idx) => (
                                        <li key={idx} className="flex items-center">
                                            <CheckCircle className="w-5 h-5 mr-3" style={{ color: colorScheme.success }} />
                                            <span className="text-gray-700 dark:text-gray-300">{feature}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    className={`w-full py-3 px-6 rounded-lg font-semibold transition-all hover:scale-105 ${plan.popular ? 'text-white shadow-lg' : 'border-2 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                                    style={plan.popular ?
                                        { backgroundColor: colorScheme.primary } :
                                        { borderColor: colorScheme.primary, color: colorScheme.primary }
                                    }
                                >
                                    {plan.name === 'Enterprise' ? 'Contact Sales' : 'Start Free Trial'}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Stats Section - enhanced */}
            <div
                className="py-20"
                style={{
                    background: `linear-gradient(135deg, ${colorScheme.primary} 0%, ${colorScheme.secondary} 100%)`
                }}
            >
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                            Trusted by Millions
                        </h2>
                        <p className="text-xl text-white/80 max-w-2xl mx-auto">
                            Join the growing community of makers building the future
                        </p>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center text-white">
                        <div className="p-6">
                            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">10M+</div>
                            <div className="text-sm sm:text-base opacity-80">Active Users</div>
                            <div className="text-xs opacity-60 mt-1">Growing 20% monthly</div>
                        </div>
                        <div className="p-6">
                            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">50M+</div>
                            <div className="text-sm sm:text-base opacity-80">Projects Created</div>
                            <div className="text-xs opacity-60 mt-1">Across 150+ countries</div>
                        </div>
                        <div className="p-6">
                            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">99.99%</div>
                            <div className="text-sm sm:text-base opacity-80">Uptime</div>
                            <div className="text-xs opacity-60 mt-1">Industry leading SLA</div>
                        </div>
                        <div className="p-6">
                            <div className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-2">24/7</div>
                            <div className="text-sm sm:text-base opacity-80">Support</div>
                            <div className="text-xs opacity-60 mt-1">Average 2min response</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section - NEW */}
            <div className="py-20 bg-white dark:bg-gray-900">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                        Ready to Transform Your Workflow?
                    </h2>
                    <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-2xl mx-auto">
                        Join thousands of teams already building amazing products. Start your free trial today - no credit card required.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                        <button
                            className="w-full sm:w-auto px-8 py-4 text-lg font-semibold text-white rounded-lg transition-all hover:scale-105 shadow-xl"
                            style={{
                                backgroundColor: colorScheme.primary,
                                boxShadow: `0 10px 25px 0 ${hexToRgba(colorScheme.primary, 0.3)}`
                            }}
                        >
                            Start Free Trial
                        </button>
                        <button className="w-full sm:w-auto px-8 py-4 text-lg font-semibold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors">
                            Schedule Demo
                        </button>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-6">
                        14-day free trial • No setup fees • Cancel anytime
                    </p>
                </div>
            </div>

            {/* Footer - enhanced */}
            <footer className="bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16">
                    <div className="grid grid-cols-2 lg:grid-cols-6 gap-8">
                        <div className="col-span-2">
                            <div className="flex items-center mb-6">
                                <div
                                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold mr-3"
                                    style={{ backgroundColor: colorScheme.primary }}
                                >
                                    B
                                </div>
                                <span className="text-xl font-semibold text-gray-900 dark:text-white">Brand</span>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-sm">
                                Empowering teams to build exceptional digital experiences with cutting-edge design and development tools.
                            </p>
                            <div className="flex space-x-4">
                                {['Twitter', 'LinkedIn', 'GitHub', 'Discord'].map((social) => (
                                    <a key={social} href="#" className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors">
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">{social[0]}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Product</h4>
                            <ul className="space-y-3 text-sm">
                                {['Features', 'Pricing', 'Templates', 'Integrations', 'API'].map((item) => (
                                    <li key={item}><a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">{item}</a></li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Company</h4>
                            <ul className="space-y-3 text-sm">
                                {['About', 'Careers', 'Blog', 'Press', 'Partners'].map((item) => (
                                    <li key={item}><a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">{item}</a></li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Resources</h4>
                            <ul className="space-y-3 text-sm">
                                {['Documentation', 'Help Center', 'Community', 'Tutorials', 'Status'].map((item) => (
                                    <li key={item}><a href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">{item}</a></li>
                                ))}
                            </ul>
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-900 dark:text-white mb-4">Contact</h4>
                            <ul className="space-y-3 text-sm">
                                <li className="flex items-center">
                                    <Mail className="w-4 h-4 mr-2" style={{ color: colorScheme.primary }} />
                                    <span className="text-gray-600 dark:text-gray-400">hello@obelisk.agency</span>
                                </li>
                                <li className="flex items-center">
                                    <Phone className="w-4 h-4 mr-2" style={{ color: colorScheme.primary }} />
                                    <span className="text-gray-600 dark:text-gray-400">+1 (555) 123-4567</span>
                                </li>
                                <li className="flex items-center">
                                    <MapPin className="w-4 h-4 mr-2" style={{ color: colorScheme.primary }} />
                                    <span className="text-gray-600 dark:text-gray-400">San Francisco, CA</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div className="border-t border-gray-200 dark:border-gray-700 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4 sm:mb-0">
                            © 2024 Obelisk. All rights reserved.
                        </p>
                        <div className="flex space-x-6 text-sm">
                            {['Privacy Policy', 'Terms of Service', 'Cookie Policy'].map((item) => (
                                <a key={item} href="#" className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">{item}</a>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );

    const renderDashboard = () => (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex">
            {/* Sidebar */}
            <div className="w-64 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700" role="complementary" aria-label="Dashboard sidebar navigation">
                <div className="p-6">
                    <div className="flex items-center">
                        <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center text-white font-bold mr-3"
                            style={{ backgroundColor: colorScheme.primary }}
                            role="img"
                            aria-label="Dashboard logo"
                        >
                            D
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Dashboard</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Analytics Hub</p>
                        </div>
                    </div>
                </div>

                {/* Navigation Menu */}
                <nav className="flex-1 p-4" role="navigation" aria-label="Dashboard navigation">
                    <div className="space-y-2">
                        {[
                            { icon: BarChart3, label: 'Overview', active: true, color: 'primary' },
                            { icon: Users, label: 'Customers', active: false, color: 'secondary' },
                            { icon: ShoppingBag, label: 'Orders', active: false, color: 'accent' },
                            { icon: TrendingUp, label: 'Analytics', active: false, color: 'success' },
                            { icon: Calendar, label: 'Calendar', active: false, color: 'warning' },
                            { icon: Settings, label: 'Settings', active: false, color: 'neutral' }
                        ].map((item, index) => (
                            <a
                                key={index}
                                href={`#${item.label.toLowerCase()}`}
                                className={`flex items-center space-x-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${item.active
                                    ? 'text-white shadow-sm'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                                    }`}
                                style={item.active ? { backgroundColor: colorScheme.primary } : {}}
                                aria-label={`Navigate to ${item.label} section`}
                                aria-current={item.active ? 'page' : undefined}
                            >
                                <item.icon className="w-5 h-5" />
                                <span>{item.label}</span>
                            </a>
                        ))}
                    </div>
                </nav>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Header */}
                <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4" role="banner">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Overview</h1>
                            <p className="text-gray-600 dark:text-gray-400">Welcome back! Here's what's happening.</p>
                        </div>
                        <div className="flex items-center space-x-4">
                            <button
                                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="View notifications"
                            >
                                <Bell className="w-5 h-5" />
                            </button>
                            <button
                                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Search dashboard"
                            >
                                <Search className="w-5 h-5" />
                            </button>
                        </div>
                    </div>
                </header>

                {/* Dashboard Content */}
                <main className="flex-1 overflow-auto p-6 custom-scrollbar" role="main">
                    {/* Stats Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                        {[
                            { label: 'Total Revenue', value: '$45,231', change: '+20.1%', icon: TrendingUp, color: colorScheme.success },
                            { label: 'New Customers', value: '2,345', change: '+180.1%', icon: Users, color: colorScheme.info },
                            { label: 'Active Orders', value: '12,234', change: '-19%', icon: ShoppingBag, color: colorScheme.warning },
                            { label: 'Conversion Rate', value: '3.24%', change: '+4.75%', icon: Target, color: colorScheme.primary }
                        ].map((stat, index) => (
                            <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700" role="img" aria-label={`${stat.label}: ${stat.value}, change: ${stat.change}`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.label}</p>
                                        <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                                        <p className="text-sm" style={{ color: stat.color }}>{stat.change}</p>
                                    </div>
                                    <div
                                        className="p-3 rounded-full"
                                        style={{ backgroundColor: hexToRgba(stat.color, 0.1) }}
                                    >
                                        <stat.icon className="w-6 h-6" style={{ color: stat.color }} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Charts Section */}
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mb-8">
                        {/* Revenue Chart - FIXED WITH DIFFERENT SIZES */}
                        <div className="xl:col-span-2 bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700" role="region" aria-labelledby="revenue-chart-title">
                            <div className="flex items-center justify-between mb-6">
                                <h3 id="revenue-chart-title" className="text-lg font-semibold text-gray-900 dark:text-white">Revenue Overview</h3>
                                <select
                                    className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-1 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                                    aria-label="Select time period for revenue chart"
                                    name="revenue-period"
                                >
                                    <option value="7days">Last 7 days</option>
                                    <option value="30days">Last 30 days</option>
                                    <option value="90days">Last 90 days</option>
                                </select>
                            </div>
                            <div className="h-64 flex items-end justify-between space-x-1 bg-gray-50 dark:bg-gray-900 rounded-lg p-4" role="img" aria-label="Revenue bar chart showing monthly data">
                                {/* FIXED: Better height calculation and more dramatic differences */}
                                {[
                                    { height: 15, value: 15000 },
                                    { height: 85, value: 85000 },
                                    { height: 25, value: 25000 },
                                    { height: 95, value: 95000 },
                                    { height: 40, value: 40000 },
                                    { height: 75, value: 75000 },
                                    { height: 20, value: 20000 },
                                    { height: 100, value: 100000 },
                                    { height: 35, value: 35000 },
                                    { height: 80, value: 80000 },
                                    { height: 30, value: 30000 },
                                    { height: 90, value: 90000 },
                                    { height: 45, value: 45000 },
                                    { height: 70, value: 70000 },
                                    { height: 60, value: 60000 }
                                ].map((bar, index) => (
                                    <div key={index} className="flex-1 flex flex-col items-center">
                                        <div
                                            className="w-full rounded-t-lg transition-all hover:opacity-80 cursor-pointer relative group"
                                            style={{
                                                height: `${Math.max(bar.height * 2, 8)}px`, // Fixed: Use px instead of % and ensure minimum height
                                                backgroundColor: index % 4 === 0 ? colorScheme.primary :
                                                    index % 4 === 1 ? colorScheme.secondary :
                                                        index % 4 === 2 ? colorScheme.accent :
                                                            colorScheme.success
                                            }}
                                            role="img"
                                            aria-label={`Revenue bar ${index + 1}: $${bar.value.toLocaleString()}`}
                                        >
                                            {/* Tooltip */}
                                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                                                ${bar.value.toLocaleString()}
                                            </div>
                                        </div>
                                        <span className="text-xs text-gray-500 dark:text-gray-400 mt-1">{index + 1}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Quick Actions - unchanged */}
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700" role="region" aria-labelledby="quick-actions-title">
                            <h3 id="quick-actions-title" className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h3>
                            <div className="space-y-3">
                                {[
                                    { icon: Plus, label: 'Create New Project', color: colorScheme.primary },
                                    { icon: Users, label: 'Invite Team Member', color: colorScheme.secondary },
                                    { icon: Download, label: 'Export Data', color: colorScheme.accent },
                                    { icon: Settings, label: 'Account Settings', color: colorScheme.neutral }
                                ].map((action, index) => (
                                    <button
                                        key={index}
                                        className="w-full flex items-center space-x-3 p-3 rounded-lg text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                        aria-label={action.label}
                                    >
                                        <div
                                            className="p-2 rounded-lg"
                                            style={{ backgroundColor: hexToRgba(action.color, 0.1) }}
                                        >
                                            <action.icon className="w-4 h-4" style={{ color: action.color }} />
                                        </div>
                                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{action.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Recent Orders Table */}
                    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" role="region" aria-labelledby="recent-orders-title">
                        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h3 id="recent-orders-title" className="text-lg font-semibold text-gray-900 dark:text-white">Recent Orders</h3>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full" role="table" aria-label="Recent orders data">
                                <thead role="rowgroup">
                                    <tr className="bg-gray-50 dark:bg-gray-900" role="row">
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" role="columnheader">Order ID</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" role="columnheader">Customer</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" role="columnheader">Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" role="columnheader">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700" role="rowgroup">
                                    {[
                                        { id: '#3210', customer: 'John Doe', amount: '$99.00', status: 'completed' },
                                        { id: '#3209', customer: 'Jane Smith', amount: '$149.00', status: 'processing' },
                                        { id: '#3208', customer: 'Mike Johnson', amount: '$79.00', status: 'shipped' },
                                        { id: '#3207', customer: 'Sarah Wilson', amount: '$199.00', status: 'pending' }
                                    ].map((order, index) => (
                                        <tr key={index} role="row">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white" role="cell">{order.id}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" role="cell">{order.customer}</td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300" role="cell">{order.amount}</td>
                                            <td className="px-6 py-4 whitespace-nowrap" role="cell">
                                                <span
                                                    className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-white"
                                                    style={{
                                                        backgroundColor: order.status === 'completed' ? colorScheme.success :
                                                            order.status === 'processing' ? colorScheme.warning :
                                                                order.status === 'shipped' ? colorScheme.info :
                                                                    order.status === 'pending' ? colorScheme.accent :
                                                                        colorScheme.neutral
                                                    }}
                                                    role="status"
                                                    aria-label={`Order status: ${order.status}`}
                                                >
                                                    {order.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );

    const renderMobileApp = () => (
        <div className="bg-gray-100 dark:bg-gray-800 p-4 flex justify-center">
            <div className="flex gap-4 xl:gap-6 max-w-6xl w-full justify-center flex-wrap xl:flex-nowrap">

                {/* FIRST Mobile Screen - Home/Dashboard */}
                <div className="w-80 h-[700px] bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden border border-gray-300 dark:border-gray-700 flex-shrink-0 flex flex-col" role="region" aria-label="Mobile app home screen">
                    {/* Status Bar */}
                    <div
                        className="h-11 flex items-center justify-between px-6 text-white text-sm font-medium flex-shrink-0"
                        style={{ backgroundColor: colorScheme.primary }}
                        role="banner"
                        aria-label="Mobile status bar"
                    >
                        <span>9:41</span>
                        <div className="flex items-center space-x-1">
                            <div className="flex space-x-1" role="img" aria-label="Signal strength indicator">
                                <div className="w-1 h-1 bg-white rounded-full opacity-60"></div>
                                <div className="w-1 h-1 bg-white rounded-full opacity-80"></div>
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                            </div>
                            <div className="ml-2 text-xs" role="img" aria-label="Battery level: 100%">100%</div>
                        </div>
                    </div>

                    {/* App Header */}
                    <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0" role="banner">
                        <div className="flex items-center justify-between">
                            <div>
                                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Good Morning</h1>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Welcome back, John!</p>
                            </div>
                            <button
                                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="View profile"
                            >
                                <User className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                            </button>
                        </div>
                    </div>

                    {/* FIXED: Added custom scrollbar to scrollable content */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar" role="main">
                        {/* Stats Cards */}
                        <div className="p-4 space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                {[
                                    { label: 'Balance', value: '$2,584', icon: Briefcase, color: colorScheme.primary },
                                    { label: 'Expenses', value: '$1,247', icon: TrendingUp, color: colorScheme.accent },
                                    { label: 'Income', value: '$4,832', icon: ArrowRight, color: colorScheme.success },
                                    { label: 'Savings', value: '$892', icon: Target, color: colorScheme.warning }
                                ].map((stat, index) => (
                                    <div key={index} className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl" role="img" aria-label={`${stat.label}: ${stat.value}`}>
                                        <div className="flex items-center justify-between mb-2">
                                            <div
                                                className="p-2 rounded-lg"
                                                style={{ backgroundColor: hexToRgba(stat.color, 0.1) }}
                                            >
                                                <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
                                            </div>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                                        <p className="text-lg font-bold text-gray-900 dark:text-white">{stat.value}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Quick Actions */}
                            <div className="mt-6">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Quick Actions</h3>
                                <div className="space-y-2">
                                    {[
                                        { icon: Send, label: 'Send Money', description: 'Transfer to contacts' },
                                        { icon: Download, label: 'Request', description: 'Ask for payment' },
                                        { icon: Plus, label: 'Add Funds', description: 'Top up your wallet' },
                                        { icon: BarChart3, label: 'Analytics', description: 'View spending insights' }
                                    ].map((action, index) => (
                                        <button
                                            key={index}
                                            className="w-full flex items-center p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                                            aria-label={`${action.label}: ${action.description}`}
                                        >
                                            <div
                                                className="p-2 rounded-lg mr-3"
                                                style={{ backgroundColor: hexToRgba(colorScheme.primary, 0.1) }}
                                            >
                                                <action.icon className="w-5 h-5" style={{ color: colorScheme.primary }} />
                                            </div>
                                            <div className="text-left">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">{action.label}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{action.description}</p>
                                            </div>
                                            <ChevronRight className="w-4 h-4 text-gray-400 ml-auto" />
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Recent Transactions */}
                            <div className="mt-6">
                                <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Recent Transactions</h3>
                                <div className="space-y-2">
                                    {[
                                        { name: 'Coffee Shop', amount: '-$4.50', time: '2 hours ago', type: 'expense' },
                                        { name: 'Salary Deposit', amount: '+$2,500', time: '1 day ago', type: 'income' },
                                        { name: 'Grocery Store', amount: '-$67.20', time: '2 days ago', type: 'expense' },
                                        { name: 'Freelance Payment', amount: '+$450', time: '3 days ago', type: 'income' }
                                    ].map((transaction, index) => (
                                        <div key={index} className="flex items-center p-3 bg-white dark:bg-gray-800 rounded-xl shadow-sm" role="listitem" aria-label={`Transaction: ${transaction.name}, ${transaction.amount}, ${transaction.time}`}>
                                            <div
                                                className="w-10 h-10 rounded-full flex items-center justify-center mr-3"
                                                style={{ backgroundColor: hexToRgba(transaction.type === 'income' ? colorScheme.success : colorScheme.accent, 0.1) }}
                                            >
                                                {transaction.type === 'income' ?
                                                    <ArrowRight className="w-4 h-4" style={{ color: colorScheme.success, transform: 'rotate(-90deg)' }} /> :
                                                    <ArrowRight className="w-4 h-4" style={{ color: colorScheme.accent, transform: 'rotate(90deg)' }} />
                                                }
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white">{transaction.name}</p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">{transaction.time}</p>
                                            </div>
                                            <p className={`text-sm font-semibold ${transaction.type === 'income' ? 'text-green-600' : 'text-red-600'}`}>
                                                {transaction.amount}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Navigation */}
                    <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4" role="navigation" aria-label="Bottom navigation">
                        <div className="flex justify-around">
                            {[
                                { icon: Home, label: 'Home', active: true },
                                { icon: BarChart3, label: 'Analytics', active: false },
                                { icon: Calendar, label: 'Calendar', active: false },
                                { icon: Settings, label: 'Settings', active: false }
                            ].map((tab, index) => (
                                <button
                                    key={index}
                                    className="flex flex-col items-center space-y-1 p-2"
                                    aria-label={`Navigate to ${tab.label}`}
                                    aria-current={tab.active ? 'page' : undefined}
                                >
                                    <div
                                        className={`p-2 rounded-lg ${tab.active ? 'text-white' : 'text-gray-400'}`}
                                        style={tab.active ? { backgroundColor: colorScheme.secondary } : {}}
                                    >
                                        <tab.icon className="w-4 h-4" />
                                    </div>
                                    <span className={`text-xs ${tab.active ? 'font-medium' : 'text-gray-500 dark:text-gray-400'}`}
                                        style={tab.active ? { color: colorScheme.secondary } : {}}>
                                        {tab.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Home Indicator */}
                    <div className="h-4 flex justify-center items-center flex-shrink-0">
                        <div className="w-32 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </div>
                </div>

                {/* SECOND Mobile Screen - Chat/Messages */}
                <div className="w-80 h-[700px] bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden border border-gray-300 dark:border-gray-700 flex-shrink-0 flex flex-col" role="region" aria-label="Mobile app messages screen">
                    {/* Status Bar */}
                    <div
                        className="h-11 flex items-center justify-between px-6 text-white text-sm font-medium flex-shrink-0"
                        style={{ backgroundColor: colorScheme.secondary }}
                        role="banner"
                        aria-label="Mobile status bar"
                    >
                        <span>9:41</span>
                        <div className="flex items-center space-x-1">
                            <div className="flex space-x-1" role="img" aria-label="Signal strength indicator">
                                <div className="w-1 h-1 bg-white rounded-full opacity-60"></div>
                                <div className="w-1 h-1 bg-white rounded-full opacity-80"></div>
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                            </div>
                            <div className="ml-2 text-xs" role="img" aria-label="Battery level: 100%">100%</div>
                        </div>
                    </div>

                    {/* Chat Header */}
                    <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0" role="banner">
                        <div className="flex items-center justify-between">
                            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Messages</h1>
                            <button
                                className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                                aria-label="Compose new message"
                            >
                                <PlusCircle className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                            </button>
                        </div>

                        {/* Search Bar */}
                        <div className="mt-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                    type="search"
                                    name="message-search"
                                    placeholder="Search messages..."
                                    className="w-full pl-10 pr-4 py-2 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:outline-none focus:ring-2 text-gray-900 dark:text-white"
                                    style={{ '--tw-ring-color': hexToRgba(colorScheme.secondary, 0.5) }}
                                    aria-label="Search through messages"
                                />
                            </div>
                        </div>
                    </div>

                    {/* FIXED: Added custom scrollbar to messages list */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar" role="main">
                        <div className="divide-y divide-gray-100 dark:divide-gray-700">
                            {[
                                { name: 'Sarah Wilson', message: 'Hey! How are you doing?', time: '2m', unread: 2, avatar: 'S' },
                                { name: 'Design Team', message: 'New mockups are ready for review', time: '15m', unread: 0, avatar: 'D', isGroup: true },
                                { name: 'Mike Johnson', message: 'Can we schedule a call tomorrow?', time: '1h', unread: 1, avatar: 'M' },
                                { name: 'Jane Smith', message: 'Thanks for the quick response!', time: '3h', unread: 0, avatar: 'J' },
                                { name: 'Project Updates', message: 'Sprint review meeting at 3 PM', time: '5h', unread: 0, avatar: 'P', isGroup: true },
                                { name: 'Alex Brown', message: 'The files you requested are attached', time: '1d', unread: 0, avatar: 'A' },
                                { name: 'Marketing Team', message: 'Campaign results look great!', time: '2d', unread: 0, avatar: 'MT', isGroup: true }
                            ].map((chat, index) => (
                                <button
                                    key={index}
                                    className="w-full flex items-center p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                                    aria-label={`Open chat with ${chat.name}. Last message: ${chat.message}. ${chat.unread ? `${chat.unread} unread messages` : 'No unread messages'}`}
                                >
                                    <div
                                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold mr-3 flex-shrink-0"
                                        style={{ backgroundColor: chat.isGroup ? colorScheme.accent : colorScheme.primary }}
                                        role="img"
                                        aria-label={`Avatar for ${chat.name}`}
                                    >
                                        {chat.avatar}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between mb-1">
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                                {chat.name}
                                            </h3>
                                            <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                                {chat.time}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                            <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                                                {chat.message}
                                            </p>
                                            {chat.unread > 0 && (
                                                <div
                                                    className="ml-2 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                                                    style={{ backgroundColor: colorScheme.danger }}
                                                    role="status"
                                                    aria-label={`${chat.unread} unread messages`}
                                                >
                                                    <span className="text-xs text-white font-semibold">{chat.unread}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Bottom Navigation */}
                    <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4" role="navigation" aria-label="Bottom navigation">
                        <div className="flex justify-around">
                            {[
                                { icon: MessageCircle, label: 'Messages', active: true },
                                { icon: Phone, label: 'Calls', active: false },
                                { icon: Users, label: 'Contacts', active: false },
                                { icon: Settings, label: 'Settings', active: false }
                            ].map((tab, index) => (
                                <button
                                    key={index}
                                    className="flex flex-col items-center space-y-1 p-2"
                                    aria-label={`Navigate to ${tab.label}`}
                                    aria-current={tab.active ? 'page' : undefined}
                                >
                                    <div
                                        className={`p-2 rounded-lg ${tab.active ? 'text-white' : 'text-gray-400'}`}
                                        style={tab.active ? { backgroundColor: colorScheme.secondary } : {}}
                                    >
                                        <tab.icon className="w-4 h-4" />
                                    </div>
                                    <span className={`text-xs ${tab.active ? 'font-medium' : 'text-gray-500 dark:text-gray-400'}`}
                                        style={tab.active ? { color: colorScheme.secondary } : {}}>
                                        {tab.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Home Indicator */}
                    <div className="h-4 flex justify-center items-center flex-shrink-0">
                        <div className="w-32 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </div>
                </div>

                {/* THIRD Mobile Screen - Shopping/E-commerce */}
                <div className="w-80 h-[700px] bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden border border-gray-300 dark:border-gray-700 flex-shrink-0 flex flex-col" role="region" aria-label="Mobile app shopping screen">
                    {/* Status Bar */}
                    <div
                        className="h-11 flex items-center justify-between px-6 text-white text-sm font-medium flex-shrink-0"
                        style={{ backgroundColor: colorScheme.accent }}
                        role="banner"
                        aria-label="Mobile status bar"
                    >
                        <span>9:41</span>
                        <div className="flex items-center space-x-1">
                            <div className="flex space-x-1" role="img" aria-label="Signal strength indicator">
                                <div className="w-1 h-1 bg-white rounded-full opacity-60"></div>
                                <div className="w-1 h-1 bg-white rounded-full opacity-80"></div>
                                <div className="w-1 h-1 bg-white rounded-full"></div>
                            </div>
                            <div className="ml-2 text-xs" role="img" aria-label="Battery level: 100%">100%</div>
                        </div>
                    </div>

                    {/* Search Header */}
                    <div className="px-6 py-3 border-b border-gray-200 dark:border-gray-700 flex-shrink-0" role="banner">
                        <div className="flex items-center space-x-3">
                            <div className="flex-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                                <div className="flex items-center px-3 py-2">
                                    <Search className="w-4 h-4 text-gray-400 mr-2" />
                                    <input
                                        type="search"
                                        name="product-search"
                                        placeholder="Search products..."
                                        className="flex-1 bg-transparent text-sm text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none"
                                        aria-label="Search for products"
                                    />
                                </div>
                            </div>
                            <button
                                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 relative"
                                aria-label="View shopping cart"
                            >
                                <ShoppingBag className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                                <div
                                    className="absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: colorScheme.danger }}
                                    role="status"
                                    aria-label="3 items in cart"
                                >
                                    <span className="text-xs text-white font-semibold">3</span>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* FIXED: Added custom scrollbar to product grid */}
                    <div className="flex-1 overflow-y-auto custom-scrollbar" role="main">
                        <div className="p-4">
                            {/* Categories */}
                            <div className="mb-6">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Categories</h2>
                                <div className="flex space-x-3 overflow-x-auto custom-scrollbar-horizontal pb-2">
                                    {[
                                        { name: 'Electronics', icon: Monitor, color: colorScheme.primary },
                                        { name: 'Fashion', icon: Heart, color: colorScheme.secondary },
                                        { name: 'Home', icon: Coffee, color: colorScheme.accent },
                                        { name: 'Sports', icon: Target, color: colorScheme.success }
                                    ].map((category, index) => (
                                        <button
                                            key={index}
                                            className="flex-shrink-0 flex flex-col items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-lg min-w-[80px]"
                                            aria-label={`Browse ${category.name} category`}
                                        >
                                            <div
                                                className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                                                style={{ backgroundColor: hexToRgba(category.color, 0.1) }}
                                            >
                                                <category.icon className="w-5 h-5" style={{ color: category.color }} />
                                            </div>
                                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300 text-center">
                                                {category.name}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Featured Products */}
                            <div className="mb-6">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">Featured</h2>
                                    <button
                                        className="text-sm font-medium"
                                        style={{ color: colorScheme.accent }}
                                        aria-label="View all featured products"
                                    >
                                        View All
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    {[
                                        { name: 'Wireless Headphones', price: '$199', rating: 4.8, image: '🎧' },
                                        { name: 'Smart Watch', price: '$299', rating: 4.6, image: '⌚' },
                                        { name: 'Laptop Stand', price: '$49', rating: 4.9, image: '💻' },
                                        { name: 'Coffee Mug', price: '$19', rating: 4.7, image: '☕' },
                                        { name: 'Phone Case', price: '$29', rating: 4.5, image: '📱' },
                                        { name: 'Desk Lamp', price: '$79', rating: 4.8, image: '💡' }
                                    ].map((product, index) => (
                                        <button
                                            key={index}
                                            className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-sm border border-gray-100 dark:border-gray-700 text-left"
                                            aria-label={`View ${product.name}, ${product.price}, rated ${product.rating} stars`}
                                        >
                                            <div className="aspect-square bg-gray-100 dark:bg-gray-700 rounded-lg mb-3 flex items-center justify-center text-2xl">
                                                {product.image}
                                            </div>
                                            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1 line-clamp-2">
                                                {product.name}
                                            </h3>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-bold" style={{ color: colorScheme.primary }}>
                                                    {product.price}
                                                </span>
                                                <div className="flex items-center space-x-1">
                                                    <Star className="w-3 h-3" style={{ color: colorScheme.warning, fill: colorScheme.warning }} />
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">{product.rating}</span>
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Special Offers */}
                            <div className="mb-4">
                                <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-3">Special Offers</h2>
                                <div
                                    className="rounded-lg p-4 text-white"
                                    style={{ backgroundColor: colorScheme.success }}
                                    role="banner"
                                    aria-label="Special offer: 30% off all electronics"
                                >
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="font-semibold mb-1">Flash Sale</h3>
                                            <p className="text-sm opacity-90">30% off all electronics</p>
                                        </div>
                                        <div className="text-right">
                                            <div className="text-xs opacity-75">Ends in</div>
                                            <div className="font-bold">2h 34m</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Bottom Navigation */}
                    <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-6 py-4" role="navigation" aria-label="Bottom navigation">
                        <div className="flex justify-around">
                            {[
                                { icon: Home, label: 'Home', active: false },
                                { icon: Search, label: 'Search', active: true },
                                { icon: Heart, label: 'Wishlist', active: false },
                                { icon: User, label: 'Profile', active: false }
                            ].map((tab, index) => (
                                <button
                                    key={index}
                                    className="flex flex-col items-center space-y-1 p-2"
                                    aria-label={`Navigate to ${tab.label}`}
                                    aria-current={tab.active ? 'page' : undefined}
                                >
                                    <div
                                        className={`p-2 rounded-lg ${tab.active ? 'text-white' : 'text-gray-400'}`}
                                        style={tab.active ? { backgroundColor: colorScheme.accent } : {}}
                                    >
                                        <tab.icon className="w-4 h-4" />
                                    </div>
                                    <span className={`text-xs ${tab.active ? 'font-medium' : 'text-gray-500 dark:text-gray-400'}`}
                                        style={tab.active ? { color: colorScheme.accent } : {}}>
                                        {tab.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Home Indicator */}
                    <div className="h-4 flex justify-center items-center flex-shrink-0">
                        <div className="w-32 h-1 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderDocument = () => (
        <div className="bg-white dark:bg-gray-900 min-h-screen">
            {/* Document Header */}
            <div className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 sticky top-0 z-10">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center space-x-4">
                            <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                <FileText className="w-5 h-5" />
                            </div>
                            <div>
                                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Digital Strategy Report</h1>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Last edited 2 hours ago • 847 views</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <button
                                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <Eye className="w-4 h-4 inline mr-2" />
                                Preview
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                            >
                                <Share2 className="w-4 h-4 inline mr-2" />
                                Share
                            </button>
                            <button
                                className="px-4 py-2 text-sm font-medium text-white rounded-lg transition-all hover:opacity-90"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                <Download className="w-4 h-4 inline mr-2" />
                                Export PDF
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Document Content */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 lg:py-12">
                <article className="prose prose-lg max-w-none dark:prose-invert">
                    {/* Document Title Section */}
                    <div className="mb-12 text-center">
                        <div
                            className="inline-block px-4 py-2 rounded-full text-sm font-medium text-white mb-6"
                            style={{ backgroundColor: colorScheme.accent }}
                        >
                            Strategic Report 2024
                        </div>
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-6">
                            Digital Transformation Strategy
                        </h1>
                        <p className="text-xl text-gray-600 dark:text-gray-400 mb-8 max-w-3xl mx-auto">
                            A comprehensive analysis of market trends, competitive landscape, and strategic recommendations for digital transformation initiatives.
                        </p>

                        {/* Author and Date Info */}
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-gray-500 dark:text-gray-400 mb-8">
                            <div className="flex items-center gap-2">
                                <div
                                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                                    style={{ backgroundColor: colorScheme.secondary }}
                                >
                                    JD
                                </div>
                                <span>John Davis, Strategy Director</span>
                            </div>
                            <span className="hidden sm:inline">•</span>
                            <div className="flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                <span>Published January 15, 2024</span>
                            </div>
                            <span className="hidden sm:inline">•</span>
                            <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4" />
                                <span>12 min read</span>
                            </div>
                        </div>
                    </div>

                    {/* Table of Contents */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 mb-12">
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                            <Menu className="w-5 h-5 mr-2" />
                            Table of Contents
                        </h2>
                        <div className="grid md:grid-cols-2 gap-2">
                            <a href="#executive-summary" className="flex items-center p-3 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                                <span className="w-6 h-6 rounded-full text-xs flex items-center justify-center mr-3 text-white" style={{ backgroundColor: colorScheme.primary }}>1</span>
                                Executive Summary
                            </a>
                            <a href="#market-analysis" className="flex items-center p-3 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                                <span className="w-6 h-6 rounded-full text-xs flex items-center justify-center mr-3 text-white" style={{ backgroundColor: colorScheme.primary }}>2</span>
                                Market Analysis
                            </a>
                            <a href="#competitive-landscape" className="flex items-center p-3 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                                <span className="w-6 h-6 rounded-full text-xs flex items-center justify-center mr-3 text-white" style={{ backgroundColor: colorScheme.primary }}>3</span>
                                Competitive Landscape
                            </a>
                            <a href="#recommendations" className="flex items-center p-3 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                                <span className="w-6 h-6 rounded-full text-xs flex items-center justify-center mr-3 text-white" style={{ backgroundColor: colorScheme.primary }}>4</span>
                                Strategic Recommendations
                            </a>
                            <a href="#implementation" className="flex items-center p-3 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                                <span className="w-6 h-6 rounded-full text-xs flex items-center justify-center mr-3 text-white" style={{ backgroundColor: colorScheme.primary }}>5</span>
                                Implementation Roadmap
                            </a>
                            <a href="#conclusion" className="flex items-center p-3 rounded-lg hover:bg-white dark:hover:bg-gray-700 transition-colors text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                                <span className="w-6 h-6 rounded-full text-xs flex items-center justify-center mr-3 text-white" style={{ backgroundColor: colorScheme.primary }}>6</span>
                                Conclusion
                            </a>
                        </div>
                    </div>

                    {/* Executive Summary */}
                    <section id="executive-summary" className="mb-16">
                        <div className="flex items-center mb-6">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-4"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                1
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Executive Summary</h2>
                        </div>

                        {/* Key Highlights */}
                        <div className="grid md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-xl">
                                <div className="flex items-center mb-4">
                                    <TrendingUp className="w-6 h-6 mr-3" style={{ color: colorScheme.primary }} />
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Growth Potential</h3>
                                </div>
                                <p className="text-2xl font-bold mb-2" style={{ color: colorScheme.primary }}>+157%</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Projected revenue increase over 3 years with digital transformation initiatives</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-900/20 p-6 rounded-xl">
                                <div className="flex items-center mb-4">
                                    <Target className="w-6 h-6 mr-3" style={{ color: colorScheme.secondary }} />
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Market Share</h3>
                                </div>
                                <p className="text-2xl font-bold mb-2" style={{ color: colorScheme.secondary }}>23.4%</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Target market share achievable through strategic positioning</p>
                            </div>
                            <div className="bg-gradient-to-br from-purple-50 to-violet-100 dark:from-purple-900/20 dark:to-violet-900/20 p-6 rounded-xl">
                                <div className="flex items-center mb-4">
                                    <Zap className="w-6 h-6 mr-3" style={{ color: colorScheme.accent }} />
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Efficiency Gains</h3>
                                </div>
                                <p className="text-2xl font-bold mb-2" style={{ color: colorScheme.accent }}>40%</p>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Operational efficiency improvement through automation</p>
                            </div>
                        </div>

                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                            Our comprehensive analysis reveals significant opportunities for digital transformation across key business areas. The market landscape has shifted dramatically, with digital-first approaches becoming essential for competitive advantage. Organizations that invest in strategic digital initiatives are positioned to capture disproportionate market share and revenue growth.
                        </p>

                        <div
                            className="border-l-4 pl-6 py-4 bg-blue-50 dark:bg-blue-900/20 rounded-r-lg mb-6"
                            style={{ borderColor: colorScheme.primary }}
                        >
                            <p className="font-medium text-gray-900 dark:text-white mb-2">Key Recommendation</p>
                            <p className="text-gray-700 dark:text-gray-300">
                                Implement a phased digital transformation approach focusing on customer experience optimization, operational automation, and data-driven decision making to achieve maximum ROI within 18 months.
                            </p>
                        </div>
                    </section>

                    {/* Market Analysis */}
                    <section id="market-analysis" className="mb-16">
                        <div className="flex items-center mb-6">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-4"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                2
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Market Analysis</h2>
                        </div>

                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                            The current market environment presents both challenges and opportunities. Digital adoption has accelerated across all industry sectors, creating new competitive dynamics and customer expectations.
                        </p>

                        {/* Market Data Table */}
                        <div className="overflow-x-auto mb-8">
                            <table className="w-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                                <thead className="border-b border-gray-200 dark:border-gray-700" style={{ backgroundColor: `${hexToRgba(colorScheme.primary, 0.1)}` }}>
                                    <tr>
                                        <th className="text-left p-4 font-semibold text-gray-900 dark:text-white">Market Segment</th>
                                        <th className="text-left p-4 font-semibold text-gray-900 dark:text-white">Growth Rate</th>
                                        <th className="text-left p-4 font-semibold text-gray-900 dark:text-white">Market Size</th>
                                        <th className="text-left p-4 font-semibold text-gray-900 dark:text-white">Opportunity</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <td className="p-4 font-medium text-gray-900 dark:text-white">E-commerce</td>
                                        <td className="p-4 text-green-600 dark:text-green-400">+24.3%</td>
                                        <td className="p-4 text-gray-700 dark:text-gray-300">$4.2B</td>
                                        <td className="p-4">
                                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs">High</span>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <td className="p-4 font-medium text-gray-900 dark:text-white">SaaS Solutions</td>
                                        <td className="p-4 text-green-600 dark:text-green-400">+31.8%</td>
                                        <td className="p-4 text-gray-700 dark:text-gray-300">$2.8B</td>
                                        <td className="p-4">
                                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs">High</span>
                                        </td>
                                    </tr>
                                    <tr className="border-b border-gray-200 dark:border-gray-700">
                                        <td className="p-4 font-medium text-gray-900 dark:text-white">Mobile Apps</td>
                                        <td className="p-4 text-blue-600 dark:text-blue-400">+18.7%</td>
                                        <td className="p-4 text-gray-700 dark:text-gray-300">$1.9B</td>
                                        <td className="p-4">
                                            <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-xs">Medium</span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td className="p-4 font-medium text-gray-900 dark:text-white">AI/Analytics</td>
                                        <td className="p-4 text-green-600 dark:text-green-400">+42.1%</td>
                                        <td className="p-4 text-gray-700 dark:text-gray-300">$3.5B</td>
                                        <td className="p-4">
                                            <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs">High</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        {/* Market Trends */}
                        <div className="grid md:grid-cols-2 gap-6 mb-8">
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Emerging Trends</h3>
                                <div className="flex items-start space-x-3">
                                    <div
                                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                                        style={{ backgroundColor: colorScheme.primary }}
                                    ></div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">AI-First Approach</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Organizations are embedding AI capabilities into core business processes</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div
                                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                                        style={{ backgroundColor: colorScheme.secondary }}
                                    ></div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Omnichannel Experience</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Seamless customer journeys across all touchpoints</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <div
                                        className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                                        style={{ backgroundColor: colorScheme.accent }}
                                    ></div>
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Sustainability Focus</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Environmental and social responsibility driving decisions</p>
                                    </div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Market Challenges</h3>
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Talent Shortage</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Limited availability of skilled digital professionals</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="w-5 h-5 text-yellow-500 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Legacy Systems</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Integration complexities with existing infrastructure</p>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <AlertCircle className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                                    <div>
                                        <p className="font-medium text-gray-900 dark:text-white">Regulatory Compliance</p>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">Evolving data privacy and security requirements</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Competitive Landscape */}
                    <section id="competitive-landscape" className="mb-16">
                        <div className="flex items-center mb-6">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-4"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                3
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Competitive Landscape</h2>
                        </div>

                        {/* Competitor Analysis Grid */}
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Market Leader A</h3>
                                    <span className="px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full text-xs">Threat</span>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Market Share</span>
                                        <span className="font-medium text-gray-900 dark:text-white">34.2%</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Revenue</span>
                                        <span className="font-medium text-gray-900 dark:text-white">$2.1B</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Growth Rate</span>
                                        <span className="font-medium text-green-600 dark:text-green-400">+22%</span>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">Strengths: Brand recognition, extensive resources</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Emerging Player B</h3>
                                    <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full text-xs">Watch</span>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Market Share</span>
                                        <span className="font-medium text-gray-900 dark:text-white">8.7%</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Revenue</span>
                                        <span className="font-medium text-gray-900 dark:text-white">$542M</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Growth Rate</span>
                                        <span className="font-medium text-green-600 dark:text-green-400">+67%</span>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">Strengths: Innovation, agility, customer focus</p>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="font-semibold text-gray-900 dark:text-white">Traditional Player C</h3>
                                    <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-xs">Opportunity</span>
                                </div>
                                <div className="space-y-3">
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Market Share</span>
                                        <span className="font-medium text-gray-900 dark:text-white">19.3%</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Revenue</span>
                                        <span className="font-medium text-gray-900 dark:text-white">$1.2B</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-sm text-gray-600 dark:text-gray-400">Growth Rate</span>
                                        <span className="font-medium text-red-600 dark:text-red-400">-3%</span>
                                    </div>
                                </div>
                                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                                    <p className="text-xs text-gray-600 dark:text-gray-400">Weaknesses: Legacy systems, slow adaptation</p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Strategic Recommendations */}
                    <section id="recommendations" className="mb-16">
                        <div className="flex items-center mb-6">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-4"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                4
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Strategic Recommendations</h2>
                        </div>

                        {/* Priority Recommendations */}
                        <div className="space-y-6 mb-8">
                            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-6">
                                <div className="flex items-start space-x-4">
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                                        style={{ backgroundColor: colorScheme.primary }}
                                    >
                                        1
                                    </div>
                                    <div className="flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Customer Experience Optimization</h3>
                                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                                            Implement omnichannel customer journey mapping and personalization engines to create seamless, engaging experiences across all touchpoints.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">High Priority</span>
                                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">6-month timeline</span>
                                            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">High ROI</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-xl p-6">
                                <div className="flex items-start space-x-4">
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                                        style={{ backgroundColor: colorScheme.secondary }}
                                    >
                                        2
                                    </div>
                                    <div className="flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Operational Automation</h3>
                                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                                            Deploy intelligent automation for repetitive processes, freeing up human resources for strategic initiatives and improving efficiency.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <span className="px-3 py-1 bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-300 rounded-full text-sm">Medium Priority</span>
                                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">9-month timeline</span>
                                            <span className="px-3 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full text-sm">Cost Savings</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-gradient-to-r from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-6">
                                <div className="flex items-start space-x-4">
                                    <div
                                        className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0"
                                        style={{ backgroundColor: colorScheme.accent }}
                                    >
                                        3
                                    </div>
                                    <div className="flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Data-Driven Decision Making</h3>
                                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                                            Build comprehensive analytics capabilities and establish data governance frameworks to enable informed strategic decisions.
                                        </p>
                                        <div className="flex flex-wrap gap-2">
                                            <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full text-sm">High Priority</span>
                                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-sm">12-month timeline</span>
                                            <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-full text-sm">Strategic Value</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Implementation Roadmap */}
                    <section id="implementation" className="mb-16">
                        <div className="flex items-center mb-6">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-4"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                5
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Implementation Roadmap</h2>
                        </div>

                        {/* Timeline */}
                        <div className="relative">
                            <div className="absolute left-4 top-0 bottom-0 w-0.5" style={{ backgroundColor: colorScheme.primary }}></div>

                            <div className="space-y-8">
                                <div className="flex items-start space-x-6">
                                    <div
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold z-10 bg-white dark:bg-gray-900"
                                        style={{ backgroundColor: colorScheme.primary }}
                                    >
                                        Q1
                                    </div>
                                    <div className="flex-grow pb-8">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Foundation Phase</h3>
                                        <p className="text-gray-700 dark:text-gray-300 mb-3">
                                            Establish digital infrastructure, team formation, and initial process assessments.
                                        </p>
                                        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-green-500" />Digital transformation team assembly</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-green-500" />Current state assessment</li>
                                            <li className="flex items-center"><CheckCircle className="w-4 h-4 mr-2 text-green-500" />Technology stack evaluation</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold z-10 bg-white dark:bg-gray-900"
                                        style={{ backgroundColor: colorScheme.secondary }}
                                    >
                                        Q2
                                    </div>
                                    <div className="flex-grow pb-8">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Implementation Phase</h3>
                                        <p className="text-gray-700 dark:text-gray-300 mb-3">
                                            Begin core system implementations and pilot program launches.
                                        </p>
                                        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                            <li className="flex items-center"><Clock className="w-4 h-4 mr-2 text-yellow-500" />Customer experience platform deployment</li>
                                            <li className="flex items-center"><Clock className="w-4 h-4 mr-2 text-yellow-500" />Automation pilot programs</li>
                                            <li className="flex items-center"><Clock className="w-4 h-4 mr-2 text-yellow-500" />Staff training and change management</li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="flex items-start space-x-6">
                                    <div
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold z-10 bg-white dark:bg-gray-900"
                                        style={{ backgroundColor: colorScheme.accent }}
                                    >
                                        Q3
                                    </div>
                                    <div className="flex-grow">
                                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Scale & Optimize</h3>
                                        <p className="text-gray-700 dark:text-gray-300 mb-3">
                                            Scale successful initiatives and optimize based on initial results.
                                        </p>
                                        <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                                            <li className="flex items-center"><AlertCircle className="w-4 h-4 mr-2 text-blue-500" />Full-scale automation rollout</li>
                                            <li className="flex items-center"><AlertCircle className="w-4 h-4 mr-2 text-blue-500" />Advanced analytics implementation</li>
                                            <li className="flex items-center"><AlertCircle className="w-4 h-4 mr-2 text-blue-500" />Performance monitoring and optimization</li>
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Call to Action */}
                    <section id="conclusion" className="mb-12">
                        <div className="flex items-center mb-6">
                            <div
                                className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold mr-4"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                6
                            </div>
                            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">Conclusion & Next Steps</h2>
                        </div>

                        <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-8">
                            The digital transformation landscape presents unprecedented opportunities for organizations ready to embrace change. Our analysis demonstrates that strategic implementation of digital initiatives can deliver significant competitive advantages and measurable business outcomes.
                        </p>

                        <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 rounded-xl p-8 text-center">
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">Ready to Begin Your Digital Transformation?</h3>
                            <p className="text-gray-600 dark:text-gray-400 mb-6">
                                Schedule a consultation to discuss implementation details and customize this strategy for your specific needs.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    className="px-6 py-3 text-white font-medium rounded-lg transition-all hover:opacity-90 flex items-center justify-center"
                                    style={{ backgroundColor: colorScheme.primary }}
                                >
                                    <Calendar className="w-4 h-4 mr-2" />
                                    Schedule Consultation
                                </button>
                                <button
                                    className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center justify-center"
                                >
                                    <Download className="w-4 h-4 mr-2" />
                                    Download Full Report
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* Document Footer */}
                    <footer className="border-t border-gray-200 dark:border-gray-700 pt-8">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                <p>© 2024 Obelisk. All rights reserved.</p>
                                <p>This document contains confidential and proprietary information.</p>
                            </div>
                            <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                <span className="flex items-center">
                                    <Eye className="w-4 h-4 mr-1" />
                                    847 views
                                </span>
                                <span className="flex items-center">
                                    <Clock className="w-4 h-4 mr-1" />
                                    Last updated 2h ago
                                </span>
                                <span className="flex items-center">
                                    <Download className="w-4 h-4 mr-1" />
                                    24 downloads
                                </span>
                            </div>
                        </div>
                    </footer>
                </article>
            </div>
        </div>
    );

    const renderEcommerce = () => (
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen p-6">
            {/* Product Page */}
            <div className="max-w-6xl mx-auto bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
                <div className="grid md:grid-cols-2 gap-8 p-8">
                    {/* Product Image */}
                    <div className="space-y-4">
                        <div className="aspect-square rounded-lg overflow-hidden" style={{ backgroundColor: hexToRgba(colorScheme.primary, 0.1) }}>
                            <div className="w-full h-full flex items-center justify-center">
                                <div className="w-48 h-48 rounded-full" style={{ backgroundColor: colorScheme.primary, opacity: 0.3 }} />
                            </div>
                        </div>
                        <div className="grid grid-cols-4 gap-2">
                            {[0, 1, 2, 3].map(i => (
                                <div key={i} className="aspect-square rounded-lg" style={{ backgroundColor: hexToRgba(getColor(i), 0.2) }} />
                            ))}
                        </div>
                    </div>

                    {/* Product Info */}
                    <div className="space-y-6">
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: colorScheme.accent }}>
                                    New Arrival
                                </span>
                                <div className="flex items-center">
                                    {[1,2,3,4,5].map(i => (
                                        <Star key={i} className="w-4 h-4" fill={colorScheme.warning} stroke="none" />
                                    ))}
                                    <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">(128 reviews)</span>
                                </div>
                            </div>
                            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Premium Product Name</h1>
                            <p className="text-2xl font-bold" style={{ color: colorScheme.primary }}>$99.99</p>
                        </div>

                        <p className="text-gray-600 dark:text-gray-400">
                            Discover exceptional quality and design with our premium product. Crafted with attention to detail and built to last.
                        </p>

                        {/* Color Options */}
                        <div>
                            <h3 className="text-sm font-semibold mb-3 text-gray-900 dark:text-white">Color Options</h3>
                            <div className="flex gap-2">
                                {colors.slice(0, 5).map((color, i) => (
                                    <button
                                        key={i}
                                        className="w-10 h-10 rounded-full border-2 border-gray-300 dark:border-gray-600 hover:scale-110 transition-transform"
                                        style={{ backgroundColor: color.value }}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Quantity */}
                        <div className="flex items-center gap-4">
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">Quantity:</span>
                            <div className="flex items-center border border-gray-300 dark:border-gray-600 rounded-lg">
                                <button className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">−</button>
                                <span className="px-6 py-2 text-gray-900 dark:text-white">1</span>
                                <button className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">+</button>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-3">
                            <button className="flex-1 px-6 py-3 rounded-lg text-white font-semibold flex items-center justify-center gap-2" style={{ backgroundColor: colorScheme.primary }}>
                                <ShoppingBag className="w-5 h-5" />
                                Add to Cart
                            </button>
                            <button className="px-6 py-3 rounded-lg border-2 font-semibold" style={{ borderColor: colorScheme.primary, color: colorScheme.primary }}>
                                <Heart className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Features */}
                        <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                            {[
                                { icon: Shield, text: '1 Year Warranty' },
                                { icon: Zap, text: 'Fast Delivery' },
                                { icon: CheckCircle, text: '100% Authentic' }
                            ].map((feature, i) => {
                                const Icon = feature.icon;
                                return (
                                    <div key={i} className="flex items-center gap-3">
                                        <Icon className="w-5 h-5" style={{ color: colorScheme.accent }} />
                                        <span className="text-sm text-gray-600 dark:text-gray-400">{feature.text}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderPricing = () => (
        <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 min-h-screen p-8">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Simple, Transparent Pricing</h1>
                    <p className="text-lg text-gray-600 dark:text-gray-400">Choose the perfect plan for your needs</p>
                </div>

                {/* Pricing Cards */}
                <div className="grid md:grid-cols-3 gap-8">
                    {[
                        { name: 'Starter', price: '$9', period: '/month', features: ['Up to 10 users', '20GB Storage', 'Basic Support', 'Mobile App'] },
                        { name: 'Professional', price: '$29', period: '/month', features: ['Up to 50 users', '100GB Storage', 'Priority Support', 'Advanced Analytics', 'API Access', 'Custom Branding'], popular: true },
                        { name: 'Enterprise', price: '$99', period: '/month', features: ['Unlimited users', '1TB Storage', '24/7 Support', 'Advanced Security', 'Dedicated Manager', 'Custom Integration'] }
                    ].map((plan, i) => (
                        <div
                            key={i}
                            className={`relative bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden transform hover:scale-105 transition-transform ${plan.popular ? 'ring-2' : ''}`}
                            style={plan.popular ? { ringColor: colorScheme.primary } : {}}
                        >
                            {plan.popular && (
                                <div className="absolute top-0 right-0 px-4 py-1 text-xs font-semibold text-white rounded-bl-lg" style={{ backgroundColor: colorScheme.accent }}>
                                    Most Popular
                                </div>
                            )}
                            <div className="p-8">
                                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                                <div className="mb-6">
                                    <span className="text-5xl font-bold" style={{ color: colorScheme.primary }}>{plan.price}</span>
                                    <span className="text-gray-500 dark:text-gray-400">{plan.period}</span>
                                </div>
                                <button
                                    className="w-full py-3 rounded-lg font-semibold text-white mb-6"
                                    style={{ backgroundColor: plan.popular ? colorScheme.primary : colorScheme.secondary }}
                                >
                                    Get Started
                                </button>
                                <ul className="space-y-3">
                                    {plan.features.map((feature, j) => (
                                        <li key={j} className="flex items-center text-gray-600 dark:text-gray-400">
                                            <CheckCircle className="w-5 h-5 mr-3" style={{ color: colorScheme.success }} />
                                            {feature}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderBlog = () => (
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen p-6">
            <div className="max-w-4xl mx-auto">
                {/* Article Header */}
                <article className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden mb-8">
                    <div className="h-64 relative" style={{ backgroundColor: hexToRgba(colorScheme.primary, 0.2) }}>
                        <div className="absolute inset-0 flex items-center justify-center">
                            <FileText className="w-24 h-24" style={{ color: colorScheme.primary, opacity: 0.3 }} />
                        </div>
                    </div>
                    <div className="p-8">
                        <div className="flex items-center gap-4 mb-4">
                            <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: colorScheme.accent }}>
                                Design
                            </span>
                            <span className="text-sm text-gray-500 dark:text-gray-400">5 min read • May 15, 2024</span>
                        </div>
                        <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
                            The Ultimate Guide to Modern Design Systems
                        </h1>
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-full" style={{ backgroundColor: colorScheme.primary }} />
                            <div>
                                <p className="font-semibold text-gray-900 dark:text-white">John Designer</p>
                                <p className="text-sm text-gray-500 dark:text-gray-400">Senior UX Designer</p>
                            </div>
                        </div>
                        <div className="prose dark:prose-invert max-w-none">
                            <p className="text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                Design systems have become an essential part of modern product development. In this comprehensive guide,
                                we'll explore the key principles and best practices for building scalable design systems.
                            </p>
                            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mt-8 mb-4">What is a Design System?</h2>
                            <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
                                A design system is a collection of reusable components, guided by clear standards, that can be assembled
                                to build any number of applications. It's more than just a component library—it's a shared language
                                that helps teams work more efficiently.
                            </p>
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-6 rounded-lg my-6" style={{ borderLeft: `4px solid ${colorScheme.accent}` }}>
                                <p className="text-gray-700 dark:text-gray-300 italic">
                                    "A design system isn't a project. It's a product serving products."
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <Heart className="w-5 h-5" />
                                <span>124</span>
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <MessageCircle className="w-5 h-5" />
                                <span>28</span>
                            </button>
                            <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
                                <Share2 className="w-5 h-5" />
                                <span>Share</span>
                            </button>
                        </div>
                    </div>
                </article>

                {/* Related Articles */}
                <div className="grid md:grid-cols-2 gap-6">
                    {[1, 2].map(i => (
                        <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden hover:shadow-lg transition-shadow">
                            <div className="h-32" style={{ backgroundColor: hexToRgba(getColor(i), 0.2) }} />
                            <div className="p-6">
                                <span className="text-xs font-semibold" style={{ color: colorScheme.accent }}>FEATURED</span>
                                <h3 className="text-xl font-bold text-gray-900 dark:text-white mt-2 mb-2">Related Article Title {i}</h3>
                                <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                                    Brief description of the related article content goes here...
                                </p>
                                <button className="text-sm font-semibold flex items-center gap-1" style={{ color: colorScheme.primary }}>
                                    Read More <ArrowRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderPortfolio = () => (
        <div className="bg-gray-50 dark:bg-gray-900 min-h-screen p-6">
            <div className="max-w-6xl mx-auto">
                {/* Hero Section */}
                <div className="text-center mb-12">
                    <div className="w-32 h-32 rounded-full mx-auto mb-6" style={{ backgroundColor: colorScheme.primary }} />
                    <h1 className="text-5xl font-bold text-gray-900 dark:text-white mb-4">Creative Portfolio</h1>
                    <p className="text-xl text-gray-600 dark:text-gray-400 mb-6">Designer • Developer • Creator</p>
                    <div className="flex justify-center gap-4">
                        {colors.slice(0, 4).map((color, i) => (
                            <button key={i} className="w-10 h-10 rounded-full" style={{ backgroundColor: color.value }} />
                        ))}
                    </div>
                </div>

                {/* Projects Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                    {colors.slice(0, 6).map((color, i) => (
                        <div key={i} className="group relative overflow-hidden rounded-xl shadow-lg hover:shadow-2xl transition-all transform hover:-translate-y-2">
                            <div className="aspect-square" style={{ backgroundColor: hexToRgba(color.value, 0.3) }}>
                                <div className="w-full h-full flex items-center justify-center">
                                    <Briefcase className="w-16 h-16" style={{ color: color.value }} />
                                </div>
                            </div>
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                                    <h3 className="text-xl font-bold mb-2">Project {i + 1}</h3>
                                    <p className="text-sm text-gray-300 mb-4">Creative design solution</p>
                                    <div className="flex gap-2">
                                        <button className="p-2 rounded-full bg-white/20 hover:bg-white/30">
                                            <Eye className="w-4 h-4" />
                                        </button>
                                        <button className="p-2 rounded-full bg-white/20 hover:bg-white/30">
                                            <Heart className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Skills Section */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Skills & Expertise</h2>
                    <div className="grid md:grid-cols-2 gap-6">
                        {[
                            { skill: 'UI/UX Design', level: 90 },
                            { skill: 'Frontend Development', level: 85 },
                            { skill: 'Graphic Design', level: 95 },
                            { skill: 'Branding', level: 80 }
                        ].map((item, i) => (
                            <div key={i}>
                                <div className="flex justify-between mb-2">
                                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">{item.skill}</span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">{item.level}%</span>
                                </div>
                                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full rounded-full transition-all duration-500"
                                        style={{ width: `${item.level}%`, backgroundColor: getColor(i) }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );

    const renderEmail = () => (
        <div className="bg-gray-100 dark:bg-gray-900 min-h-screen p-6 flex items-center justify-center">
            <div className="max-w-2xl w-full bg-white dark:bg-gray-800 rounded-lg shadow-2xl overflow-hidden">
                {/* Email Header */}
                <div className="p-8 text-center" style={{ background: `linear-gradient(135deg, ${colorScheme.primary} 0%, ${colorScheme.secondary} 100%)` }}>
                    <div className="w-16 h-16 bg-white rounded-full mx-auto mb-4 flex items-center justify-center">
                        <Mail className="w-8 h-8" style={{ color: colorScheme.primary }} />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">Welcome to Our Platform!</h1>
                    <p className="text-white/90">Thanks for joining our community</p>
                </div>

                {/* Email Body */}
                <div className="p-8">
                    <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                        Hi there,
                    </p>
                    <p className="text-gray-700 dark:text-gray-300 mb-6 leading-relaxed">
                        We're thrilled to have you on board! Your account has been successfully created, and you're now ready to
                        explore all the amazing features we have to offer.
                    </p>

                    {/* CTA Button */}
                    <div className="text-center my-8">
                        <button
                            className="px-8 py-4 rounded-lg text-white font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                            style={{ backgroundColor: colorScheme.primary }}
                        >
                            Get Started Now
                        </button>
                    </div>

                    {/* Features */}
                    <div className="grid md:grid-cols-3 gap-4 my-8">
                        {[
                            { icon: Zap, title: 'Fast Setup', desc: 'Get started in minutes' },
                            { icon: Shield, title: 'Secure', desc: 'Your data is protected' },
                            { icon: Target, title: 'Powerful', desc: 'Everything you need' }
                        ].map((feature, i) => {
                            const Icon = feature.icon;
                            return (
                                <div key={i} className="text-center p-4">
                                    <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: hexToRgba(getColor(i), 0.1) }}>
                                        <Icon className="w-6 h-6" style={{ color: getColor(i) }} />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">{feature.title}</h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{feature.desc}</p>
                                </div>
                            );
                        })}
                    </div>

                    <p className="text-gray-700 dark:text-gray-300 mb-4 leading-relaxed">
                        Need help getting started? Our support team is here to assist you 24/7.
                    </p>

                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                        Best regards,<br />
                        <strong>The Team</strong>
                    </p>
                </div>

                {/* Email Footer */}
                <div className="bg-gray-50 dark:bg-gray-900 p-6 text-center">
                    <div className="flex justify-center gap-4 mb-4">
                        {colors.slice(0, 4).map((color, i) => (
                            <button key={i} className="w-8 h-8 rounded-full" style={{ backgroundColor: color.value }} />
                        ))}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                        © 2024 Your Company. All rights reserved.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        <a href="#" className="hover:underline">Unsubscribe</a> • <a href="#" className="hover:underline">Privacy Policy</a>
                    </p>
                </div>
            </div>
        </div>
    );

    const renderAuth = () => (
        <div className="min-h-screen flex" style={{ background: `linear-gradient(135deg, ${hexToRgba(colorScheme.primary, 0.1)} 0%, ${hexToRgba(colorScheme.secondary, 0.1)} 100%)` }}>
            {/* Left Side - Branding */}
            <div className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-between" style={{ backgroundColor: colorScheme.primary }}>
                <div>
                    <div className="flex items-center gap-3 text-white mb-12">
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                            <Shield className="w-6 h-6" style={{ color: colorScheme.primary }} />
                        </div>
                        <span className="text-2xl font-bold">BrandName</span>
                    </div>
                    <h1 className="text-5xl font-bold text-white mb-6">Welcome Back</h1>
                    <p className="text-xl text-white/90 leading-relaxed">
                        Access your account and continue your journey with us.
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-6">
                    {colors.slice(0, 4).map((color, i) => (
                        <div key={i} className="bg-white/10 backdrop-blur-sm rounded-lg p-4">
                            <h3 className="text-white font-semibold mb-1">Feature {i + 1}</h3>
                            <p className="text-white/70 text-sm">Amazing benefit</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right Side - Login Form */}
            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-md">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8">
                        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Sign In</h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">Enter your credentials to access your account</p>

                        <form className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Email Address</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="email"
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                        placeholder="name@example.com"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Password</label>
                                <div className="relative">
                                    <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                    <input
                                        type="password"
                                        className="w-full pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:border-transparent dark:bg-gray-700 dark:text-white"
                                        placeholder="••••••••"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center justify-between">
                                <label className="flex items-center">
                                    <input type="checkbox" className="mr-2 rounded" style={{ accentColor: colorScheme.primary }} />
                                    <span className="text-sm text-gray-600 dark:text-gray-400">Remember me</span>
                                </label>
                                <a href="#" className="text-sm font-semibold" style={{ color: colorScheme.primary }}>Forgot password?</a>
                            </div>

                            <button
                                type="submit"
                                className="w-full py-3 rounded-lg text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all"
                                style={{ backgroundColor: colorScheme.primary }}
                            >
                                Sign In
                            </button>

                            <div className="relative my-6">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white dark:bg-gray-800 text-gray-500">Or continue with</span>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                {['Google', 'GitHub'].map((provider, i) => (
                                    <button
                                        key={i}
                                        type="button"
                                        className="px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 font-medium text-gray-700 dark:text-gray-300"
                                    >
                                        {provider}
                                    </button>
                                ))}
                            </div>
                        </form>

                        <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
                            Don't have an account? <a href="#" className="font-semibold" style={{ color: colorScheme.primary }}>Sign up</a>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderTemplateContent = () => {
        switch (activeTemplate) {
            case 'landing':
                return renderLandingPage();
            case 'dashboard':
                return renderDashboard();
            case 'document':
                return renderDocument();
            case 'mobile':
                return renderMobileApp();
            case 'ecommerce':
                return renderEcommerce();
            case 'pricing':
                return renderPricing();
            case 'blog':
                return renderBlog();
            case 'portfolio':
                return renderPortfolio();
            case 'email':
                return renderEmail();
            case 'auth':
                return renderAuth();
            default:
                return renderLandingPage();
        }
    };

    if (!colors || colors.length === 0) {
        return (
            <div className="text-center py-12">
                <Smartphone className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-400">Add some colors to your palette to see templates</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Design Templates
                </h4>
                <div className="text-sm text-gray-500 dark:text-gray-400">
                    {colors.length} colors applied
                </div>
            </div>

            {/* Template Tabs */}
            <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-2">
                <div className="flex flex-wrap gap-1">
                    {templateTabs.map(tab => {
                        const Icon = tab.icon;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTemplate(tab.id)}
                                className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-md transition-all duration-200 ${activeTemplate === tab.id
                                    ? 'bg-blue-500 text-white shadow-sm'
                                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                    }`}
                            >
                                <Icon className="w-4 h-4 mr-2" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Template Content - Full height display */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                {renderTemplateContent()}
            </div>

            {/* Template Info */}
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
                <h5 className="text-base font-semibold text-blue-900 dark:text-blue-100 mb-3">
                    💡 Template Features
                </h5>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800 dark:text-blue-200">
                    <ul className="space-y-2">
                        <li>• <strong>Container Responsive:</strong> Adapts to visualizer width changes</li>
                        <li>• <strong>Smart Color Hierarchy:</strong> Logical semantic color distribution</li>
                        <li>• <strong>Mobile Optimized:</strong> Touch-friendly interactions and layouts</li>
                    </ul>
                    <ul className="space-y-2">
                        <li>• <strong>Rich Components:</strong> Advanced UI elements and interactions</li>
                        <li>• <strong>Real-world Examples:</strong> Practical design patterns and layouts</li>
                        <li>• <strong>Accessibility Ready:</strong> WCAG compliant color contrasts</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default TemplatesDemo;